/**
 * Générateur — Automatisme : donner la valeur d'expressions numériques simples.
 * Attendu ID : 1106 | Niveau : 4ème | Domaine : Calcul littéral et algébrique
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 10, ligne 706 :
 * « Donner la valeur d'expressions numériques simples. »).
 *
 * ── Cadrage (sous-agent programme-maths, brouillon 2026-09-12) ─────────────
 * Automatisation de l'objectif 5e l. 499/503 (priorités, parenthèses) sur
 * des ENTIERS POSITIFS : deux opérations, une priorité ou une paire de
 * parenthèses, trois nombres. « Simple » borne les nombres et la longueur.
 * Exclus : relatifs dans un enchaînement (objectif 4e l. 643), sommes de
 * relatifs seules (1091), une seule opération (cycle 3, 1045), vocabulaire
 * somme/produit (l. 644), programme de calcul en mots (160) ; facteur commun
 * 12 × 7 + 12 × 3 (DEPP q.8 : distributivité, objectif 5e l. 504), carré
 * dans l'expression (objectif 5e l. 593), décimaux : non par défaut, À
 * ARBITRER.
 * MathsMentales (S. Cogez, Apache 2.0, relevé 2026-09-12) : 6ND11 (« − suivi
 * de + », « + suivi de × », « ÷ suivi de × », « × suivi de ÷ », a 5..50,
 * b, c 2..10) et 6ND12 (produit par une somme, quotient d'une somme,
 * différence entre un nombre et une somme) : les mêmes formes, entières.
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « Série flash » (flash_texte_trou_saisie) : quatre lignes KaTeX,
 *     un entier à saisir : une priorité × ou ÷ sans parenthèses ; a − b × c ;
 *     une paire de parenthèses qui CHANGE le résultat ; une forme libre.
 *   Carte 2 « Quel résultat ? » (resultat_qcm_unique) : QUATRE choix ; sans
 *     parenthèses : 14 (clé), 20 (gauche_a_droite, LA principale : DEPP 4e
 *     2023 q.17, 22,6 %), 12 (un_seul_terme), 9 (signes_confondus) ; avec
 *     parenthèses : 20 (clé), 32 (parentheses_ignorees), 9, 35. Valeurs
 *     distinctes par tirage, la clé encadrée quand les familles le
 *     permettent (résidu mesuré à la revue).
 *   Carte 3 « Vrai ou faux » (sens_tableau_vf) : quatre égalités, chacune
 *     étiquetée par sa VALEUR (assert), une à trois vraies.
 *
 * INVARIANTS : entiers 1..50, produits et quotients dans les tables (b, c
 * de 2 à 10), résultat entier 0..100 ; par série, une ligne où lire de
 * gauche à droite se trompe et une ligne où les parenthèses changent le
 * résultat ; chaque expression évaluée par deux chemins (throw).
 * SACRIFIÉ (fiche d'aide à venir, deux pages aux mêmes nombres : 1 + 3 × 8
 * et (1 + 3) × 8) : le triplet (1 ; 3 ; 8), sous toutes ses formes,
 * auto-testé ; l'item DEPP q.17 ne sort jamais en carte (cohérent avec
 * 1108 et 1111).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, tex,
    retirerCodesErreur,
} from '../../../utils';

const ATTENDU_ID = 1106;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_QCM = 'Quel résultat ?';
const L_VF = 'Vrai ou faux';

// ─── Le domaine ───────────────────────────────────────────────────────────

/** Les nombres de la fiche, sacrifiés sous toutes leurs formes. */
export const TRIPLET_SACRIFIE = [1, 3, 8];
const estSacrifie = (nombres) => nombres.length === 3 && [...nombres].sort((u, v) => u - v).join(',') === [...TRIPLET_SACRIFIE].sort((u, v) => u - v).join(',');

/**
 * Les formes : `tex(a, b, c)` écrit l'expression, `val` la calcule avec les
 * priorités, `gauche` la lit de gauche à droite (l'erreur principale),
 * `parentheses` dit si la forme en porte. Toutes à trois nombres.
 */
const FORMES = {
    plus_fois: { tex: (a, b, c) => `${a} + ${b} \\times ${c}`, val: (a, b, c) => a + b * c, gauche: (a, b, c) => (a + b) * c, parentheses: false },
    moins_fois: { tex: (a, b, c) => `${a} - ${b} \\times ${c}`, val: (a, b, c) => a - b * c, gauche: (a, b, c) => (a - b) * c, parentheses: false },
    // fois_plus, fois_moins, div_plus : lire de gauche à droite donne la bonne valeur (pas un piège : jamais seules dans une série)
    fois_plus: { tex: (a, b, c) => `${a} \\times ${b} + ${c}`, val: (a, b, c) => a * b + c, gauche: (a, b, c) => a * b + c, parentheses: false, piege: false },
    fois_moins: { tex: (a, b, c) => `${a} \\times ${b} - ${c}`, val: (a, b, c) => a * b - c, gauche: (a, b, c) => a * b - c, parentheses: false, piege: false },
    plus_div: { tex: (a, b, c) => `${a} + ${b} \\div ${c}`, val: (a, b, c) => a + b / c, gauche: (a, b, c) => (a + b) / c, parentheses: false },
    div_plus: { tex: (a, b, c) => `${a} \\div ${b} + ${c}`, val: (a, b, c) => a / b + c, gauche: (a, b, c) => a / b + c, parentheses: false, piege: false },
    par_plus_fois: { tex: (a, b, c) => `(${a} + ${b}) \\times ${c}`, val: (a, b, c) => (a + b) * c, sans: (a, b, c) => a + b * c, parentheses: true },
    fois_par_moins: { tex: (a, b, c) => `${a} \\times (${b} - ${c})`, val: (a, b, c) => a * (b - c), sans: (a, b, c) => a * b - c, parentheses: true },
    par_plus_div: { tex: (a, b, c) => `(${a} + ${b}) \\div ${c}`, val: (a, b, c) => (a + b) / c, sans: (a, b, c) => a + b / c, parentheses: true },
    moins_par_plus: { tex: (a, b, c) => `${a} - (${b} + ${c})`, val: (a, b, c) => a - (b + c), sans: (a, b, c) => a - b + c, parentheses: true },
};

/** Second chemin de l'assert : évaluation de l'écriture KaTeX par un mini-parseur (parenthèses, puis × ÷, puis + −). */
const evaluer = (ecriture) => {
    const s = ecriture.replace(/\\times/g, '*').replace(/\\div/g, '/').replace(/\s/g, '');
    const paren = s.match(/^(.*)\(([^()]+)\)(.*)$/);
    if (paren) return evaluer(`${paren[1]}${evaluer(paren[2])}${paren[3]}`);
    const termes = s.split(/(?=[+-])/).filter(Boolean);
    return termes.reduce((acc, t) => {
        const signe = t.startsWith('-') ? -1 : 1;
        const facteurs = t.replace(/^[+-]/, '').split(/(?=[*/])/);
        const v = facteurs.reduce((p, f) => (f.startsWith('*') ? p * Number(f.slice(1)) : f.startsWith('/') ? p / Number(f.slice(1)) : Number(f)), 1);
        return acc + signe * v;
    }, 0);
};

/** Un item { forme, a, b, c, ecriture, valeur } dans le domaine ; null si le tirage échoue. */
const item = (nom, a, b, c) => {
    const f = FORMES[nom];
    const nombres = [a, b, c];
    if (nombres.some((n) => !Number.isInteger(n) || n < 1 || n > 50)) return null;
    const valeur = f.val(a, b, c);
    if (!Number.isInteger(valeur) || valeur < 0 || valeur > 100) return null;
    if (estSacrifie(nombres)) return null;
    const ecriture = f.tex(a, b, c);
    if (evaluer(ecriture) !== valeur) throw new Error(`gen_1106 : ${ecriture} vaut ${evaluer(ecriture)}, pas ${valeur}`);
    // toute forme sans parenthèses doit piéger la lecture de gauche à droite, toute forme avec parenthèses doit en dépendre
    const autre = f.parentheses ? f.sans(a, b, c) : f.gauche(a, b, c);
    if (f.piege !== false && autre === valeur) return null;
    return { forme: nom, a, b, c, ecriture, valeur };
};

const tirer = (noms = Object.keys(FORMES)) => {
    for (let essai = 0; essai < 200; essai++) {
        const nom = randomChoice(noms);
        const f = FORMES[nom];
        let a; let b; let c;
        if (nom === 'plus_div' || nom === 'par_plus_div') { c = randInt(2, 10); b = nom === 'plus_div' ? c * randInt(2, 10) : randInt(1, 40); a = nom === 'plus_div' ? randInt(1, 50) : c * randInt(1, 10) - b; }
        else if (nom === 'div_plus') { b = randInt(2, 10); a = b * randInt(2, 10); c = randInt(1, 50); }
        else if (nom === 'fois_par_moins') { a = randInt(2, 10); b = randInt(3, 12); c = randInt(1, b - 1); }
        else if (nom === 'moins_par_plus') { b = randInt(1, 30); c = randInt(1, 30); a = b + c + randInt(0, 30); }
        else { a = randInt(1, 50); b = randInt(2, 10); c = randInt(2, 10); }
        if (f.parentheses === false && (nom === 'moins_fois') && a < b * c) continue;
        if (nom === 'fois_moins' && a * b < c) continue;
        const it = item(nom, a, b, c);
        if (it) return it;
    }
    return item('plus_fois', 2, 3, 4);
};

const EXPLICATIONS = {
    gauche_a_droite: 'On ne calcule pas de gauche à droite : la multiplication et la division d\'abord, l\'addition et la soustraction ensuite.',
    un_seul_terme: 'Il manque un terme : 2 + 3 × 4, c\'est 2 plus 12, pas seulement 12.',
    signes_confondus: 'Ce n\'est pas la même opération : × n\'est pas +.',
    parentheses_ignorees: 'Les parenthèses d\'abord : 5 × (7 − 3), c\'est 5 × 4.',
    regle: 'Les parenthèses d\'abord, puis les multiplications et les divisions, puis les additions et les soustractions.',
};

// ─── Codes d'erreur (contenu.erreurs, lus par trouverErreurs) ─────────────

/** Le code commun de chaque famille (catalogue validé) ; signes_confondus a été rejeté : jamais rangé. */
const CODE_COMMUN = { gauche_a_droite: 'gauche_a_droite', parentheses_ignorees: 'parentheses_ignorees', un_seul_terme: 'un_seul_terme', signes_confondus: null };

/** Clé de comparaison d'une réponse : sa valeur si c'est un nombre (5,25 = 5.250), son texte sinon. */
const cleReponse = (v) => {
    const s = String(v).replace(/\s/g, '').replace(',', '.');
    return s !== '' && Number.isFinite(Number(s)) ? `n${Number(s)}` : `t${s.toLowerCase()}`;
};

/** Les erreurs nommables parmi des candidats { code, valeur } (code null : famille rejetée) : jamais la bonne réponse, jamais une valeur que deux codes donnent. */
const nommer = (candidats, bonne) => {
    const groupes = new Map();
    candidats.forEach((c) => {
        const k = cleReponse(c.valeur);
        if (k !== cleReponse(bonne)) groupes.set(k, [...(groupes.get(k) || []), c]);
    });
    return [...groupes.values()]
        .filter((g) => g[0].code && g.every((c) => c.code === g[0].code))
        .map((g) => ({ code: g[0].code, valeur: String(g[0].valeur) }));
};

/** Range les erreurs nommées dans la question (rien si aucune). */
const ranger = (payload, erreurs) => {
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};

/** Un résultat écrit comme l'élève le saisit (5,25) ; null s'il ne s'écrit pas en deux décimales au plus (22 ÷ 3). */
const ecrire = (v) => (Number.isFinite(v) && Math.abs(v * 100 - Math.round(v * 100)) < 1e-9 ? String(Math.round(v * 100) / 100).replace('.', ',') : null);

/** Le code commun d'une lecture fautive. Relecture du 04/10/2026 : sans parenthèses dans l'écriture
 * (8 × 9 + 2 lu 8 × 11), « parentheses_ignorees » ne dit pas l'erreur ; sans phrase validée, pas de nom. */
const codeDe = (it, famille) => (famille === 'parentheses_ignorees' && !FORMES[it.forme].parentheses ? null : CODE_COMMUN[famille]);

/** Les erreurs nommables d'un item : ses trois lectures fautives (la table du QCM), dont les valeurs se saisissent. */
const erreursItem = (it) => nommer(famillesFausses(it)
    .map(([v, code]) => ({ code: codeDe(it, code), valeur: ecrire(v) }))
    .filter((c) => c.valeur !== null), String(it.valeur));

/** Les leurres d'un QCM ({ valeur, code du gen }) dont l'erreur est nommable : le texte exact du choix, son code commun. */
const erreursQcm = (leurres, nommees) => leurres
    .map((l) => ({ code: CODE_COMMUN[l.code], valeur: String(l.valeur) }))
    .filter((l) => l.code && nommees.some((e) => e.code === l.code && cleReponse(e.valeur) === cleReponse(l.valeur)));

/** Vrai/faux : chaque égalité fausse dont le membre de droite est une lecture fautive nommable de son expression. */
const erreursVf = (items, lignes, verites) => lignes.flatMap((ligne, i) => {
    const droite = (ligne.match(/ = (\d+)\$$/) || [])[1];
    const e = verites[i] ? null : erreursItem(items[i]).find((x) => cleReponse(x.valeur) === cleReponse(droite));
    return e ? [{ code: e.code, ligne }] : [];
});

// ─── Carte 1 : Série flash ────────────────────────────────────────────────

const SANS = Object.keys(FORMES).filter((n) => !FORMES[n].parentheses);
const AVEC = Object.keys(FORMES).filter((n) => FORMES[n].parentheses);

const assemblerFlash = (items) => {
    const lignes = items.map((it) => `$${it.ecriture} = {{${it.valeur}}}$`);
    const payload = makeTexteTrou(lignes.join('\n'), '<p><b>Calcule.</b></p>', { ...opts(L_FLASH), mode: 'saisie' });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle, gauche_a_droite: EXPLICATIONS.gauche_a_droite } };
    // une ligne, un trou : les lectures fautives de chaque expression
    ranger(payload, items.flatMap((it, trou) => erreursItem(it).map(({ code, valeur }) => ({ code, trou, valeur }))));
    payload._meta.params = { items };
    return payload;
};

const genFlash = () => {
    for (let essai = 0; essai < 80; essai++) {
        // une priorité qui piège la lecture de gauche à droite, une forme à − ou à produit en tête, une paire de parenthèses, une libre
        const items = [tirer(['plus_fois', 'plus_div']), tirer(['moins_fois', 'fois_moins', 'fois_plus', 'div_plus']), tirer(AVEC), tirer()];
        if (new Set(items.map((it) => it.ecriture)).size < 4 || new Set(items.map((it) => it.valeur)).size < 4) continue;
        return assemblerFlash(shuffle(items));
    }
    return assemblerFlash([item('plus_fois', 2, 3, 4), item('moins_fois', 20, 2, 5), item('par_plus_fois', 2, 3, 4), item('div_plus', 8, 2, 2)]);
};

// ─── Carte 2 : QCM « Quel résultat ? » ────────────────────────────────────

const famillesFausses = (it) => {
    const { a, b, c } = it;
    // Trois familles par forme, chacune une lecture fautive de CETTE écriture
    // (jamais une opération que l'écriture ne porte pas).
    const PAR_FORME = {
        plus_fois: [[(a + b) * c, 'gauche_a_droite'], [b * c, 'un_seul_terme'], [a + b + c, 'signes_confondus']],
        moins_fois: [[(a - b) * c, 'gauche_a_droite'], [b * c, 'un_seul_terme'], [a - b - c, 'signes_confondus']],
        fois_plus: [[a * (b + c), 'parentheses_ignorees'], [a * b, 'un_seul_terme'], [a + b + c, 'signes_confondus']],
        fois_moins: [[a * (b - c), 'parentheses_ignorees'], [a * b, 'un_seul_terme'], [a + b - c, 'signes_confondus']],
        plus_div: [[(a + b) / c, 'gauche_a_droite'], [b / c, 'un_seul_terme'], [a + b - c, 'signes_confondus']],
        div_plus: [[a / (b + c), 'parentheses_ignorees'], [a / b, 'un_seul_terme'], [a - b + c, 'signes_confondus']],
        par_plus_fois: [[a + b * c, 'parentheses_ignorees'], [b * c, 'un_seul_terme'], [a + b + c, 'signes_confondus']],
        fois_par_moins: [[a * b - c, 'parentheses_ignorees'], [b - c, 'un_seul_terme'], [a * (b + c), 'signes_confondus']],
        par_plus_div: [[a + b / c, 'parentheses_ignorees'], [a + b, 'un_seul_terme'], [(a + b) - c, 'signes_confondus']],
        moins_par_plus: [[a - b + c, 'parentheses_ignorees'], [a - b, 'un_seul_terme'], [a + b + c, 'signes_confondus']],
    };
    return PAR_FORME[it.forme];
};

const distracteurs = (it) => {
    const { valeur } = it;
    const cands = famillesFausses(it).map(([v, code]) => ({ valeur: v, code }));
    const vals = new Set([valeur, ...cands.map((x) => x.valeur)]);
    if (vals.size !== 4 || cands.some((x) => !Number.isInteger(x.valeur) || x.valeur < 0)) return null;
    return cands;
};

const genQcm = () => {
    for (let essai = 0; essai < 120; essai++) {
        const it = tirer();
        const faux = distracteurs(it);
        if (!faux) continue;
        const bon = String(it.valeur);
        const payload = makeQcm([bon, ...faux.map((x) => String(x.valeur))], bon, `<p><b>Combien vaut ${tex(it.ecriture)} ?</b></p>`, opts(L_QCM));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        ranger(payload, erreursQcm(faux, erreursItem(it)));
        payload._meta.params = { ...it, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    const it = item('plus_fois', 2, 3, 4);
    const payload = makeQcm(['14', '20', '12', '9'], '14', `<p><b>Combien vaut ${tex(it.ecriture)} ?</b></p>`, opts(L_QCM));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    ranger(payload, erreursQcm(distracteurs(it), erreursItem(it)));
    payload._meta.params = { ...it, distracteurs: [{ code: 'gauche_a_droite' }, { code: 'un_seul_terme' }, { code: 'signes_confondus' }] };
    return payload;
};

// ─── Carte 3 : Vrai ou faux (4 égalités) ──────────────────────────────────

const genVf = () => {
    for (let essai = 0; essai < 120; essai++) {
        const items = [tirer(SANS), tirer(SANS), tirer(AVEC), tirer()];
        if (new Set(items.map((it) => it.ecriture)).size < 4) continue;
        const nbVraies = randInt(1, 3);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(4 - nbVraies).fill(false)]);
        const lignes = items.map((it, i) => {
            if (verites[i]) return { ligne: tex(`${it.ecriture} = ${it.valeur}`), code: null };
            const faux = distracteurs(it);
            if (!faux) return null;
            const d = randomChoice(faux);
            return { ligne: tex(`${it.ecriture} = ${d.valeur}`), code: d.code };
        });
        if (lignes.some((l) => !l)) continue;
        lignes.forEach((l, i) => {
            const m = l.ligne.replace(/\\mathrm\{\}/g, '').replace(/[$]/g, '').match(/^(.+) = (\d+)$/);
            if (!m || (evaluer(m[1]) === Number(m[2])) !== verites[i]) throw new Error(`gen_1106 : ligne mal étiquetée ${l.ligne}`);
        });
        const payload = makeVraiFaux(lignes.map((l) => l.ligne), verites.map((v) => (v ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        ranger(payload, erreursVf(items, lignes.map((l) => l.ligne), verites));
        payload._meta.params = { items, verites, distracteurs: lignes.filter((l) => l.code).map(({ code }) => ({ code })) };
        return payload;
    }
    const items = [item('plus_fois', 2, 3, 4), item('par_plus_fois', 2, 3, 4), item('moins_fois', 20, 2, 5), item('div_plus', 8, 2, 2)];
    const lignes = [tex('2 + 3 \\times 4 = 20'), tex('(2 + 3) \\times 4 = 20'), tex('20 - 2 \\times 5 = 10'), tex('8 \\div 2 + 2 = 2')];
    const payload = makeVraiFaux(lignes, [1, 0, 0, 1], '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    ranger(payload, erreursVf(items, lignes, [false, true, true, false]));
    payload._meta.params = { items, verites: [false, true, true, false], distracteurs: [{ code: 'gauche_a_droite' }, { code: 'gauche_a_droite' }] };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'quatre valeurs entières positives distinctes, sinon nouveau tirage ; jamais d\'option inventée',
        'le triplet sacrifié (1 ; 3 ; 8) ne sort d\'aucune carte sous aucune forme',
    ],
    qcm_unique: {
        support: 'une expression à trois entiers et deux opérations, dix formes (six sans parenthèses, quatre avec)',
        familles: {
            gauche_a_droite: 'lecture de gauche à droite (2 + 3 × 4 → 20), la principale (DEPP 4e 2023 q.17 : 22,6 %)',
            un_seul_terme: 'un terme oublié (→ 12 ; DEPP q.8 : 84 pour 12 × 7 + 12 × 3)',
            signes_confondus: '× lu + (→ 9)',
            parentheses_ignorees: 'les parenthèses non lues (5 × (7 − 3) → 32)',
        },
    },
    vf_tableau_vf: 'quatre égalités (deux sans parenthèses, une avec, une libre), chaque fausse tire une famille de son item, étiquetage vérifié en valeur',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    resultat_qcm_unique: genQcm,
    sens_tableau_vf: genVf,
};

export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'resultat', label: L_QCM, formatsList: [{ key: 'resultat_qcm_unique', label: 'QCM' }] },
    { id: 'sens', label: L_VF, formatsList: [{ key: 'sens_tableau_vf', label: 'Vrai/Faux' }] },
];

// Codes retirés le 03/10/2026 après la lecture à l'aveugle (docs/kit/REGLES_EQUIPE.md) : le nom ne
// décrit pas le choix construit, ou deux erreurs donnent le même nombre. Le choix reste proposé,
// il part sans nom.
const CODES_RETIRES = {
    resultat_qcm_unique: ['parentheses_ignorees'],
};

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    retirerCodesErreur(payload, CODES_RETIRES[key]);
    payload._meta.generatorKey = key;
    return payload;
};
